/**
 * cfg-zoom.js
 * Zoom for the configurator's static product photos (not the 360 spin).
 *
 * - Desktop (fine pointer + hover): moving over the main photo shows a
 *   magnified copy inside the frame that follows the cursor.
 * - Click / tap the photo (or the magnifier button) to open a fullscreen
 *   lightbox: pinch, double-tap, scroll-wheel or click to zoom, drag to
 *   pan, swipe or arrow keys to change photo, Esc to close.
 *
 * Opt-in per section: the frame carries data-cfg-zoom="<hover scale>".
 * Kept separate from sofa-configurator.js so a failure here can never
 * take the configurator down with it.
 */
(function () {
  'use strict';

  var HIRES_WIDTH = 2400;   // CDN width requested for zoomed views
  var LB_MAX_SCALE = 4;
  var DOUBLE_TAP_MS = 300;
  var SWIPE_PX = 60;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function sizedUrl(url, width) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + width);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  // Same file regardless of the width/v query — used to find the main
  // photo's position in the thumbnail list.
  function fileKey(url) {
    try { return new URL(url, location.href).pathname; } catch (e) { return url; }
  }

  function mainImage(frame) {
    return frame.querySelector('[data-cfg-main-image]') || frame.querySelector('img');
  }

  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  /* ── Hover lens (desktop) ─────────────────────────────── */

  var bound = typeof WeakSet === 'function' ? new WeakSet() : null;

  function bindFrame(frame) {
    if (!bound || bound.has(frame)) return;
    bound.add(frame);

    var lens = document.createElement('div');
    lens.className = 'cfg-zoom-lens';
    lens.setAttribute('aria-hidden', 'true');
    frame.appendChild(lens);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cfg-zoom-btn';
    btn.setAttribute('aria-label', 'Zoom image');
    btn.innerHTML = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M11 8v6M8 11h6"/></svg>';
    frame.appendChild(btn);

    var scale = parseFloat(frame.getAttribute('data-cfg-zoom')) || 2.5;
    var active = false;

    function move(e) {
      var r = frame.getBoundingClientRect();
      var x = clamp((e.clientX - r.left) / r.width, 0, 1) * 100;
      var y = clamp((e.clientY - r.top) / r.height, 0, 1) * 100;
      lens.style.backgroundPosition = x + '% ' + y + '%';
    }

    frame.addEventListener('mouseenter', function (e) {
      if (!canHover.matches) return;
      var img = mainImage(frame);
      if (!img) return;
      var lo = img.currentSrc || img.src;
      // Low-res layer underneath paints instantly; the hi-res one covers
      // it as soon as it arrives.
      lens.style.backgroundImage = 'url("' + sizedUrl(lo, HIRES_WIDTH) + '"), url("' + lo + '")';
      lens.style.backgroundSize = (scale * 100) + '% auto';
      active = true;
      move(e);
      frame.classList.add('is-zooming');
    });
    frame.addEventListener('mousemove', function (e) { if (active) move(e); });
    frame.addEventListener('mouseleave', function () {
      active = false;
      frame.classList.remove('is-zooming');
    });

    frame.addEventListener('click', function (e) {
      if (!mainImage(frame)) return;
      e.preventDefault();
      frame.classList.remove('is-zooming');
      active = false;
      Lightbox.open(frame);
    });
  }

  function scan() {
    document.querySelectorAll('[data-cfg-flat][data-cfg-zoom]').forEach(bindFrame);
  }

  /* ── Fullscreen lightbox ──────────────────────────────── */

  var Lightbox = {
    el: null,
    slides: [],
    index: 0,
    s: 1, tx: 0, ty: 0,
    pointers: {},
    gesture: null,
    lastTap: null,
    returnFocus: null,

    build: function () {
      if (this.el) return;
      var el = document.createElement('div');
      el.className = 'cfg-lb';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Product image zoom');
      el.hidden = true;
      el.innerHTML =
        '<div class="cfg-lb__stage" data-lb-stage><img class="cfg-lb__img" data-lb-img alt="" draggable="false"></div>' +
        '<div class="cfg-lb__bar">' +
          '<span class="cfg-lb__counter" data-lb-counter></span>' +
          '<span class="cfg-lb__hint" data-lb-hint></span>' +
        '</div>' +
        '<button type="button" class="cfg-lb__btn cfg-lb__close" data-lb-close aria-label="Close zoom">' +
          '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<button type="button" class="cfg-lb__btn cfg-lb__nav cfg-lb__nav--prev" data-lb-prev aria-label="Previous image">' +
          '<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg></button>' +
        '<button type="button" class="cfg-lb__btn cfg-lb__nav cfg-lb__nav--next" data-lb-next aria-label="Next image">' +
          '<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg></button>' +
        '<div class="cfg-lb__zoom">' +
          '<button type="button" class="cfg-lb__btn" data-lb-zoom="-1" aria-label="Zoom out">' +
            '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/></svg></button>' +
          '<button type="button" class="cfg-lb__btn" data-lb-zoom="1" aria-label="Zoom in">' +
            '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button>' +
        '</div>';
      document.body.appendChild(el);

      this.el = el;
      this.stage = el.querySelector('[data-lb-stage]');
      this.img = el.querySelector('[data-lb-img]');
      this.counter = el.querySelector('[data-lb-counter]');
      this.hint = el.querySelector('[data-lb-hint]');
      this.prevBtn = el.querySelector('[data-lb-prev]');
      this.nextBtn = el.querySelector('[data-lb-next]');

      var self = this;
      el.querySelector('[data-lb-close]').addEventListener('click', function () { self.close(); });
      this.prevBtn.addEventListener('click', function () { self.step(-1); });
      this.nextBtn.addEventListener('click', function () { self.step(1); });
      el.querySelectorAll('[data-lb-zoom]').forEach(function (b) {
        b.addEventListener('click', function () {
          var dir = parseInt(b.getAttribute('data-lb-zoom'), 10);
          self.zoomTo(self.s * (dir > 0 ? 1.6 : 1 / 1.6), 0, 0, true);
        });
      });

      this.stage.addEventListener('pointerdown', this.onDown.bind(this));
      this.stage.addEventListener('pointermove', this.onMove.bind(this));
      this.stage.addEventListener('pointerup', this.onUp.bind(this));
      this.stage.addEventListener('pointercancel', this.onUp.bind(this));
      this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      el.addEventListener('keydown', this.onKey.bind(this));
      window.addEventListener('resize', function () { if (!self.el.hidden) self.fit(); });
    },

    collect: function (frame) {
      var slides = [];
      document.querySelectorAll('[data-cfg-thumb]').forEach(function (t) {
        if (t.dataset.imageSrc) slides.push({ src: t.dataset.imageSrc, alt: t.dataset.imageAlt || '' });
      });
      var img = mainImage(frame);
      var cur = img.currentSrc || img.src;
      var key = fileKey(cur);
      var index = -1;
      for (var i = 0; i < slides.length; i++) {
        if (fileKey(slides[i].src) === key) { index = i; break; }
      }
      // The confirmed fabric's photo isn't always one of the thumbnails.
      if (index === -1) {
        slides.unshift({ src: cur, alt: img.alt || '' });
        index = 0;
      }
      this.slides = slides;
      this.index = index;
    },

    open: function (frame) {
      this.build();
      this.collect(frame);
      this.returnFocus = document.activeElement;
      this.el.hidden = false;
      this.el.classList.toggle('is-single', this.slides.length < 2);
      document.documentElement.classList.add('cfg-lb-open');
      this.show(this.index);
      this.el.querySelector('[data-lb-close]').focus({ preventScroll: true });
    },

    close: function () {
      if (!this.el || this.el.hidden) return;
      this.el.hidden = true;
      document.documentElement.classList.remove('cfg-lb-open');
      this.pointers = {};
      this.gesture = null;
      if (this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
    },

    step: function (dir) {
      if (this.slides.length < 2) return;
      this.show((this.index + dir + this.slides.length) % this.slides.length);
    },

    show: function (i) {
      var self = this;
      var slide = this.slides[i];
      this.index = i;
      this.counter.textContent = this.slides.length > 1 ? (i + 1) + ' / ' + this.slides.length : '';
      this.hint.textContent = canHover.matches ? 'Click or scroll to zoom' : 'Pinch or double-tap to zoom';
      this.img.alt = slide.alt;
      this.maxScale = LB_MAX_SCALE;
      this.reset(false);

      // Show the cached low-res photo at once, swap in hi-res when ready.
      var token = {};
      this.loadToken = token;
      this.img.onload = function () { if (self.loadToken === token) self.fit(); };
      this.img.src = slide.src;
      var hi = new Image();
      hi.onload = function () {
        if (self.loadToken !== token) return;
        self.img.src = hi.src;
        self.maxScale = clamp(hi.naturalWidth / (self.baseW || 1), 2, LB_MAX_SCALE);
      };
      hi.src = sizedUrl(slide.src, HIRES_WIDTH);
    },

    // Size the image to fit the stage at scale 1 so pan limits match the
    // visible photo rather than an object-fit box.
    fit: function () {
      var nw = this.img.naturalWidth, nh = this.img.naturalHeight;
      if (!nw || !nh) return;
      var r = this.stage.getBoundingClientRect();
      var k = Math.min(r.width / nw, r.height / nh);
      this.baseW = Math.round(nw * k);
      this.baseH = Math.round(nh * k);
      this.img.style.width = this.baseW + 'px';
      this.img.style.height = this.baseH + 'px';
      this.clampPan();
      this.apply(false);
    },

    reset: function (animate) {
      this.s = 1; this.tx = 0; this.ty = 0;
      this.apply(animate);
    },

    apply: function (animate) {
      this.img.classList.toggle('is-animating', !!animate);
      this.img.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.s + ')';
      this.el.classList.toggle('is-zoomed', this.s > 1.01);
    },

    clampPan: function () {
      var r = this.stage.getBoundingClientRect();
      var mx = Math.max(0, (this.baseW * this.s - r.width) / 2);
      var my = Math.max(0, (this.baseH * this.s - r.height) / 2);
      this.tx = clamp(this.tx, -mx, mx);
      this.ty = clamp(this.ty, -my, my);
    },

    // (qx, qy): point relative to the stage centre that stays put.
    zoomTo: function (s, qx, qy, animate) {
      var next = clamp(s, 1, this.maxScale || LB_MAX_SCALE);
      var k = next / this.s;
      this.tx = qx - (qx - this.tx) * k;
      this.ty = qy - (qy - this.ty) * k;
      this.s = next;
      this.clampPan();
      this.apply(animate);
    },

    local: function (x, y) {
      var r = this.stage.getBoundingClientRect();
      return { x: x - (r.left + r.width / 2), y: y - (r.top + r.height / 2) };
    },

    onDown: function (e) {
      if (e.button && e.button !== 0) return;
      this.stage.setPointerCapture(e.pointerId);
      this.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(this.pointers);
      if (ids.length === 1) {
        this.gesture = {
          type: 'drag', x0: e.clientX, y0: e.clientY, tx0: this.tx, ty0: this.ty,
          t0: Date.now(), moved: false, pointerType: e.pointerType, target: e.target
        };
      } else if (ids.length === 2) {
        var a = this.pointers[ids[0]], b = this.pointers[ids[1]];
        var mid = this.local((a.x + b.x) / 2, (a.y + b.y) / 2);
        this.gesture = {
          type: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          s0: this.s, mx0: mid.x, my0: mid.y, tx0: this.tx, ty0: this.ty
        };
      }
    },

    onMove: function (e) {
      if (!this.pointers[e.pointerId]) return;
      this.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var g = this.gesture;
      if (!g) return;

      if (g.type === 'pinch') {
        var ids = Object.keys(this.pointers);
        if (ids.length < 2) return;
        var a = this.pointers[ids[0]], b = this.pointers[ids[1]];
        var mid = this.local((a.x + b.x) / 2, (a.y + b.y) / 2);
        var s = clamp(g.s0 * Math.hypot(a.x - b.x, a.y - b.y) / g.d0, 1, this.maxScale || LB_MAX_SCALE);
        var k = s / g.s0;
        this.s = s;
        this.tx = mid.x - (g.mx0 - g.tx0) * k;
        this.ty = mid.y - (g.my0 - g.ty0) * k;
        this.clampPan();
        this.apply(false);
        return;
      }

      var dx = e.clientX - g.x0, dy = e.clientY - g.y0;
      if (!g.moved && Math.hypot(dx, dy) > 6) g.moved = true;
      if (!g.moved) return;
      if (this.s > 1.01) {
        this.tx = g.tx0 + dx;
        this.ty = g.ty0 + dy;
        this.clampPan();
      } else {
        // Not zoomed: horizontal drag previews a swipe to the next photo.
        this.tx = this.slides.length > 1 ? dx : 0;
        this.ty = 0;
      }
      this.apply(false);
    },

    onUp: function (e) {
      if (!this.pointers[e.pointerId]) return;
      delete this.pointers[e.pointerId];
      var g = this.gesture;
      var left = Object.keys(this.pointers).length;

      if (g && g.type === 'pinch') {
        // Lifting one finger of a pinch hands over to a pan with the other.
        if (left === 1) {
          var id = Object.keys(this.pointers)[0], p = this.pointers[id];
          this.gesture = { type: 'drag', x0: p.x, y0: p.y, tx0: this.tx, ty0: this.ty, t0: 0, moved: true };
        } else {
          this.gesture = null;
        }
        if (this.s < 1.05) this.reset(true);
        return;
      }
      if (!g || left) return;
      this.gesture = null;

      if (g.moved) {
        if (this.s <= 1.01) {
          var dx = e.clientX - g.x0;
          if (Math.abs(dx) > SWIPE_PX && this.slides.length > 1) this.step(dx < 0 ? 1 : -1);
          else this.reset(true);
        }
        return;
      }
      if (e.type === 'pointercancel') return;
      this.tap(e, g);
    },

    tap: function (e, g) {
      var q = this.local(e.clientX, e.clientY);
      var onImage = g.target === this.img;

      if (g.pointerType === 'mouse') {
        // Mouse: a click on the photo toggles zoom; on the empty backdrop
        // at fit size it closes, like most lightboxes.
        if (this.s > 1.01) this.reset(true);
        else if (onImage) this.zoomTo(2.5, q.x, q.y, true);
        else this.close();
        return;
      }

      var now = Date.now();
      var last = this.lastTap;
      if (last && now - last.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30) {
        this.lastTap = null;
        if (this.s > 1.01) this.reset(true);
        else this.zoomTo(2.5, q.x, q.y, true);
      } else {
        this.lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
    },

    onWheel: function (e) {
      e.preventDefault();
      var q = this.local(e.clientX, e.clientY);
      var delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      this.zoomTo(this.s * Math.exp(-delta * 0.0025), q.x, q.y, false);
    },

    onKey: function (e) {
      switch (e.key) {
        case 'Escape': e.preventDefault(); this.close(); break;
        case 'ArrowLeft': if (this.s <= 1.01) { e.preventDefault(); this.step(-1); } break;
        case 'ArrowRight': if (this.s <= 1.01) { e.preventDefault(); this.step(1); } break;
        case '+': case '=': e.preventDefault(); this.zoomTo(this.s * 1.6, 0, 0, true); break;
        case '-': case '_': e.preventDefault(); this.zoomTo(this.s / 1.6, 0, 0, true); break;
        case 'Tab': this.trapFocus(e); break;
      }
    },

    trapFocus: function (e) {
      var items = Array.prototype.filter.call(
        this.el.querySelectorAll('button'),
        function (b) { return b.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ── Init ─────────────────────────────────────────────── */

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // The configurator swaps page content on size switch and the theme
  // editor re-renders sections — bind any new frames as they appear.
  if (window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; scan(); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
